import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { getConfig } from "../lib/api";
import ProductImage from "../components/ProductImage";
import Seo from "../components/Seo";
import { eur } from "../lib/format";
import { Minus, Plus, Trash2, ArrowRight, ShoppingBag, Truck, Gift } from "lucide-react";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const [cfg, setCfg] = useState(null);
  const nav = useNavigate();

  useEffect(() => { getConfig().then(setCfg).catch(() => {}); }, []);

  const today = new Date().toISOString().slice(0, 10);
  const autoOn = cfg && cfg.auto_promo_enabled && cfg.auto_promo_threshold
    && (!cfg.auto_promo_start || cfg.auto_promo_start <= today)
    && (!cfg.auto_promo_end || cfg.auto_promo_end >= today);
  const remaining = autoOn ? Math.max(0, cfg.auto_promo_threshold - subtotal) : 0;
  const autoLabel = autoOn ? (cfg.auto_promo_label || "descuento automático") : "";

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <Seo title="Carrito | FABRIPAN" description="Revise su pedido antes de confirmar." noindex />
      <h1 className="font-heading font-bold text-4xl tracking-tighter mb-6">Su pedido</h1>

      {items.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-border rounded-sm">
          <ShoppingBag className="h-12 w-12 mx-auto text-muted-foreground/40" />
          <p className="mt-4 text-muted-foreground">Su carrito está vacío.</p>
          <Link to="/tienda" className="inline-flex items-center gap-2 mt-4 bg-brand text-white px-6 py-3 rounded-sm font-semibold hover:bg-brand-dark transition-colors" data-testid="empty-cart-shop">
            Ir a la tienda <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_320px] gap-8">
          <div className="space-y-3">
            {items.map((it) => (
              <div key={it.product_id} className="flex gap-4 border border-border rounded-sm bg-white p-3" data-testid={`cart-item-${it.product_id}`}>
                <Link to={`/producto/${it.slug}`} className="w-20 h-20 shrink-0 bg-secondary rounded-sm overflow-hidden">
                  <ProductImage src={it.image} alt={it.name} className="w-full h-full object-contain p-1" />
                </Link>
                <div className="flex-1 min-w-0">
                  {it.reference && <span className="text-[10px] font-mono text-muted-foreground">REF {it.reference}</span>}
                  <Link to={`/producto/${it.slug}`} className="block font-medium text-sm leading-snug hover:text-brand line-clamp-2">{it.name}</Link>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center border border-border rounded-sm">
                      <button onClick={() => updateQty(it.product_id, it.quantity - 1)} className="p-1.5 hover:bg-secondary" data-testid={`cart-minus-${it.product_id}`}><Minus className="h-3 w-3" /></button>
                      <span className="w-10 text-center text-sm font-medium">{it.quantity}</span>
                      <button onClick={() => updateQty(it.product_id, it.quantity + 1)} className="p-1.5 hover:bg-secondary" data-testid={`cart-plus-${it.product_id}`}><Plus className="h-3 w-3" /></button>
                    </div>
                    {it.min_qty > 1 && <span className="text-[10px] text-amber-700 ml-2" data-testid={`cart-min-${it.product_id}`}>Mín. {it.min_qty} uds.</span>}
                    <div className="flex items-center gap-3">
                      <span className="font-heading font-semibold">{eur(it.price * it.quantity)}</span>
                      <button onClick={() => removeItem(it.product_id)} className="text-muted-foreground hover:text-destructive" data-testid={`cart-remove-${it.product_id}`} aria-label="Eliminar"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <aside className="h-fit border border-border rounded-sm bg-white p-5 sticky top-24">
            <h2 className="font-heading font-semibold text-lg mb-4">Resumen</h2>
            {autoOn && remaining > 0 && (
              <div className="mb-4 bg-brand/5 border border-brand/30 rounded-sm p-3 text-xs flex items-start gap-2" data-testid="cart-promo-hint">
                <Gift className="h-4 w-4 text-brand shrink-0 mt-0.5" />
                <span>Te faltan <strong>{eur(remaining)}</strong> para conseguir <strong>{autoLabel}</strong>.</span>
              </div>
            )}
            {autoOn && remaining === 0 && (
              <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-xs flex items-start gap-2 text-emerald-700" data-testid="cart-promo-ok">
                <Gift className="h-4 w-4 shrink-0 mt-0.5" />
                <span>¡Genial! Tu pedido ya tiene <strong>{autoLabel}</strong>. Se aplicará en el checkout.</span>
              </div>
            )}
            <div className="flex justify-between text-sm mb-2"><span className="text-muted-foreground">Base imponible</span><span className="font-mono">{eur(subtotal)}</span></div>
            <div className="flex justify-between text-sm mb-2"><span className="text-muted-foreground">IVA (21%)</span><span className="font-mono">{eur(subtotal * 0.21)}</span></div>
            <div className="flex justify-between font-heading font-bold text-lg border-t border-border pt-3 mt-3"><span>Total</span><span className="text-brand">{eur(subtotal * 1.21)}</span></div>
            <button onClick={() => nav("/checkout")} className="w-full mt-5 inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-dark text-white font-semibold py-3 rounded-sm transition-colors" data-testid="go-checkout-btn">
              Tramitar pedido <ArrowRight className="h-4 w-4" />
            </button>
            <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5"><Truck className="h-3.5 w-3.5" /> Enviamos su pedido personalizado a su empresa.</p>
          </aside>
        </div>
      )}
    </div>
  );
}
